import React, { useState } from 'react';
import { Copy, Code, Brain, ExternalLink, CheckCircle, FileText, Terminal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
// Toast functionality replaced with simple alerts

export interface CursorSuccessModalContentProps {
  onClose: () => void;
  integration: any;
}

export const CursorSuccessModalContent: React.FC<CursorSuccessModalContentProps> = ({
  onClose,
  integration,
}) => {
  const { t } = useTranslation();
  // Removed useToast - using simple alerts instead
  const [activeTab, setActiveTab] = useState<'cursor-setup' | 'vscode-setup' | 'testing'>('cursor-setup');
  const [copiedStates, setCopiedStates] = useState<{ [key: string]: boolean }>({});

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [key]: true }));
      alert(t('cursor.copiedSuccess', { ns: 'integrations' }) as string);
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [key]: false }));
      }, 2000);
    } catch (error) {
      alert(t('cursor.copyFailed', { ns: 'integrations' }) as string);
    }
  };

  if (!integration) return null;

  const currentHost = window.location.host;
  const mcpHost = currentHost.startsWith('app.')
    ? `mcp.${currentHost.slice(4)}`
    : currentHost.replace(/^ui\./, 'mcp.');
  const mcpOrigin = `${window.location.protocol}//${mcpHost}`;
  const endpointUrl = `${mcpOrigin}/mcp`;

  // Cursor Desktop Configuration
  const cursorConfig = {
    "mcpServers": {
      [`memara-${integration.integration_name.toLowerCase().replace(/\s+/g, '-')}`]: {
        "transport": {
          "type": "http",
          "url": endpointUrl
        }
      }
    }
  };

  // VS Code with Cursor Configuration
  const vscodeConfig = {
    "mcpServers": {
      [`memara-${integration.integration_name.toLowerCase().replace(/\s+/g, '-')}`]: {
        "transport": {
          "type": "http",
          "url": endpointUrl
        }
      }
    }
  };

  // cURL Test Command
  const testCommand = `curl "${mcpOrigin}/.well-known/oauth-authorization-server"`;

  const tabs = [
    {
      id: 'cursor-setup',
      label: 'Cursor Setup',
      icon: Code,
      badge: 'Recommended'
    },
    {
      id: 'vscode-setup',
      label: 'VS Code Setup',
      icon: FileText,
      badge: 'Alternative'
    },
    {
      id: 'testing',
      label: 'Testing',
      icon: Terminal,
      badge: 'Verify'
    }
  ];

  return (
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-gray-200 p-6 pr-12 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-gradient-to-r from-blue-600 to-cyan-600 p-2">
              <CheckCircle className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Cursor Integration Ready! 🎯
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Your coding memory system is configured and ready to use
              </p>
            </div>
          </div>
        </div>

        {/* Integration Info */}
        <div className="flex-shrink-0 border-b border-gray-200 bg-blue-50 p-6 dark:border-gray-700 dark:bg-blue-900/20">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {integration.integration_name}
              </h3>
              <p className="text-sm text-gray-500">
                Socket: {integration.socket_id} • Type: Cursor IDE MCP (OAuth)
              </p>
            </div>
            <div className="flex space-x-2">
              <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                ✅ Active
              </Badge>
              <Badge variant="outline">
                MCP Protocol
              </Badge>
            </div>
          </div>

          <Alert>
            <Brain className="h-4 w-4" />
            <AlertDescription>
              <strong>Coding Features Enabled:</strong>{' '}
              {integration.auth_config?.permissions?.code_sessions && 'Session Capture, '}
              {integration.auth_config?.permissions?.ai_conversations && 'AI Interactions, '}
              {integration.auth_config?.permissions?.debugging_insights && 'Debug Insights, '}
              {integration.auth_config?.permissions?.project_context && 'Project Context'}
            </AlertDescription>
          </Alert>
        </div>

        {/* Content */}
        <div className="flex min-h-0 flex-1 overflow-hidden">
          {/* Sidebar */}
          <div className="w-64 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 p-4">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-sm font-medium">{tab.label}</span>
                    {tab.badge && (
                      <Badge 
                        variant={activeTab === tab.id ? "default" : "secondary"}
                        className="ml-auto text-xs"
                      >
                        {tab.badge}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === 'cursor-setup' && (
              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Cursor IDE Setup
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Configure Cursor IDE to use Memara's memory system via Streamable HTTP + OAuth.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                      Step 1: Open Cursor Settings
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        1. Open Cursor IDE<br/>
                        2. Go to <strong>Settings</strong> → <strong>Features</strong> → <strong>Model Context Protocol</strong><br/>
                        3. Enable MCP if not already enabled
                      </p>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                      Step 2: Add Memara MCP Server
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          MCP Server Configuration
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(JSON.stringify(cursorConfig, null, 2), 'cursor-config')}
                          className="text-xs"
                        >
                          {copiedStates['cursor-config'] ? (
                            <CheckCircle className="h-3 w-3 mr-1" />
                          ) : (
                            <Copy className="h-3 w-3 mr-1" />
                          )}
                          {copiedStates['cursor-config'] ? 'Copied!' : 'Copy'}
                        </Button>
                      </div>
                      <pre className="text-xs bg-white dark:bg-gray-800 p-3 rounded border overflow-x-auto">
                        <code>{JSON.stringify(cursorConfig, null, 2)}</code>
                      </pre>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                      Step 3: Restart Cursor
                    </h4>
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        Restart Cursor IDE to load the new MCP server configuration.
                      </p>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                      Available Commands in Cursor
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                        <div><code className="bg-white dark:bg-gray-800 px-2 py-1 rounded">store_memory</code> - Save memories and coding insights</div>
                        <div><code className="bg-white dark:bg-gray-800 px-2 py-1 rounded">search_memories</code> - Find past solutions and context</div>
                        <div><code className="bg-white dark:bg-gray-800 px-2 py-1 rounded">list_memories</code> - View recent memory activity</div>
                        <div><code className="bg-white dark:bg-gray-800 px-2 py-1 rounded">get_memory</code> - Get a memory by ID</div>
                        <div><code className="bg-white dark:bg-gray-800 px-2 py-1 rounded">update_memory</code> - Update a memory by ID</div>
                        <div><code className="bg-white dark:bg-gray-800 px-2 py-1 rounded">delete_memory</code> - Delete a memory by ID</div>
                        <div><code className="bg-white dark:bg-gray-800 px-2 py-1 rounded">get_server_info</code> - Check integration status</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'vscode-setup' && (
              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    VS Code Alternative Setup
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    If you're using VS Code instead of Cursor, use any MCP-capable extension with the same OAuth HTTP endpoint.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                      Settings Configuration
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          VS Code Settings (settings.json)
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(JSON.stringify(vscodeConfig, null, 2), 'vscode-config')}
                          className="text-xs"
                        >
                          {copiedStates['vscode-config'] ? (
                            <CheckCircle className="h-3 w-3 mr-1" />
                          ) : (
                            <Copy className="h-3 w-3 mr-1" />
                          )}
                          {copiedStates['vscode-config'] ? 'Copied!' : 'Copy'}
                        </Button>
                      </div>
                      <pre className="text-xs bg-white dark:bg-gray-800 p-3 rounded border overflow-x-auto">
                        <code>{JSON.stringify(vscodeConfig, null, 2)}</code>
                      </pre>
                    </div>
                  </div>

                  <Alert>
                    <ExternalLink className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Note:</strong> Use an MCP-capable VS Code extension that supports OAuth over HTTP transport.
                    </AlertDescription>
                  </Alert>
                </div>
              </div>
            )}

            {activeTab === 'testing' && (
              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Test Your Integration
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Verify your OAuth discovery endpoint and run an end-to-end check in Cursor.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                      Test OAuth Discovery with cURL
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Discovery Command
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(testCommand, 'test-command')}
                          className="text-xs"
                        >
                          {copiedStates['test-command'] ? (
                            <CheckCircle className="h-3 w-3 mr-1" />
                          ) : (
                            <Copy className="h-3 w-3 mr-1" />
                          )}
                          {copiedStates['test-command'] ? 'Copied!' : 'Copy'}
                        </Button>
                      </div>
                      <pre className="text-xs bg-white dark:bg-gray-800 p-3 rounded border overflow-x-auto">
                        <code>{testCommand}</code>
                      </pre>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                      Test in Cursor IDE
                    </h4>
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                      <div className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
                        <p>1. Open Cursor IDE with the MCP configuration</p>
                        <p>2. Start a chat and try: <code className="bg-white dark:bg-blue-800 px-2 py-1 rounded">"Run get_server_info"</code></p>
                        <p>3. Try storing a memory: <code className="bg-white dark:bg-blue-800 px-2 py-1 rounded">"Run store_memory with content: console.log('Hello Memara')"</code></p>
                        <p>4. Search for it: <code className="bg-white dark:bg-blue-800 px-2 py-1 rounded">"Run search_memories for console.log"</code></p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                      Expected Response
                    </h4>
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                      <p className="text-sm text-green-700 dark:text-green-300">
                        ✅ You should receive OAuth authorization-server metadata JSON from the MCP host.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Need help? Check the <Button variant="link" className="p-0 h-auto font-normal text-blue-600">documentation</Button> or reach out to support.
            </div>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => window.open('/integrations/cursor', '_blank')}
                size="sm"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View Docs
              </Button>
              <Button onClick={onClose} className="bg-gradient-to-r from-blue-600 to-cyan-600">
                Done
              </Button>
            </div>
          </div>
        </div>
      </div>
  );
};

interface CursorSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  integration: any;
}

export const CursorSuccessModal: React.FC<CursorSuccessModalProps> = ({
  isOpen,
  onClose,
  integration,
}) => {
  if (!integration) return null;
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent variant="scrollable" className="max-w-5xl dark:bg-gray-800">
        <CursorSuccessModalContent integration={integration} onClose={onClose} />
      </DialogContent>
    </Dialog>
  );
};

